/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Yup from 'yup';
import { __ } from 'i18n';
import { SelectOption } from '@7shifts/sous-chef';

const VARIABLE_REGEX = /\{\{\d+\}\}/g;

function countVariables(text: string): number {
    const matches = text.match(VARIABLE_REGEX);
    return matches ? matches.length : 0;
}

function startsWithVariable(text: string): boolean {
    return /^\s*\{\{\d+\}\}/.test(text);
}

function endsWithVariable(text: string): boolean {
    return /\{\{\d+\}\}\s*$/.test(text);
}

function hasConsecutiveVariables(text: string): boolean {
    return /\{\{\d+\}\}\s*\{\{\d+\}\}/.test(text);
}

function hasExcessiveSpaces(text: string): boolean {
    return /\s{5,}/.test(text);
}

export const MAX_BODY_VARIABLES = 6;
export const MAX_HEADER_VARIABLES = 1;

export const getSchema = () => {
    return Yup.object().shape({
        name: Yup.string()
            .max(512)
            .required(__('messageTemplateForm.nameRequired'))
            .matches(
                /^([a-z0-9_]*)$/g,
                __('messageTemplateForm.templateNameFormat')
            ),
        type: Yup.object().required(__('messageTemplateForm.typeRequired')),
        headerContent: Yup.string().when('headerType', {
            is: (value: SelectOption<string>) => value.value === 'text',
            then: () =>
                Yup.string()
                    .min(5, __('messageTemplateForm.headerTextMinimal'))
                    .required(__('messageTemplateForm.headerTextRequired'))
                    .test(
                        'header-max-vars',
                        __('messageTemplateForm.headerMaxVariables'),
                        (value) => {
                            if (!value) return true;
                            return (
                                countVariables(value) <= MAX_HEADER_VARIABLES
                            );
                        }
                    )
                    .test(
                        'header-no-start-var',
                        __('messageTemplateForm.varCannotStart'),
                        (value) => {
                            if (!value) return true;
                            return !startsWithVariable(value);
                        }
                    )
                    .test(
                        'header-no-end-var',
                        __('messageTemplateForm.varCannotEnd'),
                        (value) => {
                            if (!value) return true;
                            return !endsWithVariable(value);
                        }
                    )
        }),
        bodyText: Yup.string()
            .min(5, __('messageTemplateForm.bodyTextMin'))
            .max(1024, __('messageTemplateForm.bodyTextMax'))
            .required(__('messageTemplateForm.bodyTextRequired'))
            .test(
                'body-max-vars',
                __('messageTemplateForm.bodyMaxVariables'),
                (value) => {
                    if (!value) return true;
                    return countVariables(value) <= MAX_BODY_VARIABLES;
                }
            )
            .test(
                'body-no-start-var',
                __('messageTemplateForm.varCannotStart'),
                (value) => {
                    if (!value) return true;
                    return !startsWithVariable(value);
                }
            )
            .test(
                'body-no-end-var',
                __('messageTemplateForm.varCannotEnd'),
                (value) => {
                    if (!value) return true;
                    return !endsWithVariable(value);
                }
            )
            .test(
                'body-no-consecutive-vars',
                __('messageTemplateForm.varCannotConsecutive'),
                (value) => {
                    if (!value) return true;
                    return !hasConsecutiveVariables(value);
                }
            )
            .test(
                'body-no-excessive-spaces',
                __('messageTemplateForm.excessiveSpaces'),
                (value) => {
                    if (!value) return true;
                    return !hasExcessiveSpaces(value);
                }
            ),
        footerText: Yup.string().test(
            'is-filled',
            __('messageTemplateForm.footerTextRequired'),
            (value) => {
                return !value || value.length > 5;
            }
        ),
        buttons: Yup.array()
            .of(
                Yup.string().required(__('messageTemplateForm.buttonsRequired'))
            )
            .max(3, __('messageTemplateForm.maxButtons'))
    });
};
